import {
  Button,
  Form,
  Modal,
  FormInstance,
  ModalProps,
  FormProps,
  Flex,
} from 'antd'
import React, {
  useCallback,
  useState,
  useMemo,
  PropsWithChildren,
  useEffect,
} from 'react'

export interface FormModalProps<Values> extends PropsWithChildren {
  form: FormInstance<Values>
  onSubmit: (values: Values, form: FormInstance<Values>) => Promise<void> | void
  open: boolean
  setOpen: (open: boolean) => void
  cancelText?: string
  submitText?: string
  formProps?: FormProps<Values>
  modalProps?: ModalProps
}

export default function FormModal<Values>({
  form,
  onSubmit,
  open,
  setOpen,
  submitText,
  cancelText,
  modalProps,
  formProps,
  children,
}: FormModalProps<Values>) {
  const [submitting, setSubmitting] = useState(false)
  const submit = useCallback(async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()
      await onSubmit(values, form)
      setOpen(false)
      setSubmitting(false)
    } catch (e) {
      console.warn('Submission error:', e)
      setSubmitting(false)
    }
  }, [form, onSubmit, setOpen])
  useEffect(() => {
    if (!open) {
      form.resetFields()
    }
  }, [form, open, formProps?.initialValues])
  const onCancel = useCallback(() => {
    setOpen(false)
  }, [setOpen])
  const footer = useMemo(
    () => (
      <Flex justify="end" gap={10}>
        <Button htmlType="button" onClick={onCancel} type="primary" ghost>
          {cancelText || 'Cancel'}
        </Button>
        <Button
          htmlType="button"
          type="primary"
          loading={submitting}
          onClick={submit}
          {...modalProps?.okButtonProps}
        >
          {submitText || 'Submit'}
        </Button>
      </Flex>
    ),
    [
      cancelText,
      modalProps?.okButtonProps,
      onCancel,
      submit,
      submitText,
      submitting,
    ],
  )

  return (
    <Modal
      forceRender
      open={open}
      footer={footer}
      onCancel={onCancel}
      closeIcon={false}
      {...modalProps}
    >
      <Form<Values> form={form} {...formProps}>
        {children}
      </Form>
    </Modal>
  )
}
